import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentIcon from '@mui/icons-material/Payment';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import AutocompleteSelect from '../../../Widgets/AutocompleteSelect/AutocompleteSelect';
import CustomMonthDatePicker from '../../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import ReportsService from '../../../../Services/reports.service.js';
import { beautifyNumber } from '../../../../Utils/FormatNumber';
import NoDocumentsPdf from '../../../../assests/pdf/NoDocument.pdf';

export const PAYMENT_METHODS = [
    { value: 2, key: 'BankTransaction' },
    { value: 1, key: 'Cash' },
    { value: 0, key: 'Check' },
    { value: 3, key: 'CreditCard' },
];

const Toolbar = styled('div')(() => ({
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%)',
    border: '1px solid rgba(102, 126, 234, 0.2)',
    borderRadius: '10px',
    marginBottom: '8px',
}));

const ActionButton = styled(Button)(() => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '6px',
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'none',
    color: '#fff',
    minHeight: '32px',
    '&:hover': {
        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
    },
    '&.Mui-disabled': {
        color: '#fff',
        opacity: 0.5,
    },
}));

const ParentRow = styled(TableRow)(() => ({
    backgroundColor: '#f5f7ff',
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#eef1ff' },
    '& > td': { fontWeight: 700, fontSize: '13px', borderBottom: '1px solid #d8ddff' },
}));

const ChildRow = styled(TableRow)(() => ({
    '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.04)' },
    '& > td': { fontSize: '12px', paddingTop: 4, paddingBottom: 4 },
}));

const rowKey = (row) => `${row.sourceType}:${row.sourceId}`;

const formatIsoDate = (d) => {
    if (!d) return null;
    const parsed = dayjs(d);
    return parsed.isValid() ? parsed.toISOString() : null;
};

const mapTransaction = (item) => ({
    id: `${item.sourceType || item.SourceType}:${item.sourceId || item.SourceId}`,
    sourceType: item.sourceType || item.SourceType,
    sourceId: item.sourceId || item.SourceId,
    supplierCardId: item.supplierCardId || item.SupplierCardId,
    supplierCardName: item.supplierCardName || item.SupplierCardName,
    uploadNumber: item.uploadNumber ?? item.UploadNumber,
    amount: item.amount ?? item.Amount ?? 0,
    withholdingTax: item.withholdingTax ?? item.WithholdingTax ?? 0,
    documentDate: item.documentDate || item.DocumentDate,
    reportingMonthDate: item.reportingMonthDate || item.ReportingMonthDate,
    registrationMonthDate: item.registrationMonthDate || item.RegistrationMonthDate,
    reference: item.reference || item.Reference || '',
    description: item.description || item.Description || '',
    documentUrl: item.documentUrl || item.DocumentUrl,
    accountCaseId: item.accountCaseId || item.AccountCaseId,
    status: 'pending',
});

const mapSupplierGroup = (g) => {
    const transactions = (g.transactions || g.Transactions || []).map(mapTransaction);
    return {
        supplierCardId: g.supplierCardId || g.SupplierCardId,
        supplierCardName: g.supplierCardName || g.SupplierCardName,
        initialDebit: g.initialDebit ?? g.InitialDebit ?? 0,
        initialCredit: g.initialCredit ?? g.InitialCredit ?? 0,
        totalDebit: g.totalDebit ?? g.TotalDebit ?? 0,
        totalCredit: g.totalCredit ?? g.TotalCredit ?? 0,
        balance: g.balance ?? g.Balance ?? 0,
        isClosed: g.isClosed ?? g.IsClosed ?? false,
        unpaidCount: g.unpaidCount ?? g.UnpaidCount ?? transactions.length,
        unpaidAmount: g.unpaidAmount ?? g.UnpaidAmount ?? 0,
        transactions,
    };
};

const inlineFieldSx = {
    '& .MuiInputBase-root': { height: 28, fontSize: 12 },
    '& .MuiInputBase-input': { padding: '4px 8px', fontSize: 12 },
};

/**
 * Create-payments tab: suppliers as expandable parents with ledger debit/credit/balance,
 * unpaid expense/JE children underneath.
 */
const WithholdingUnpaidPaymentsTab = ({
    t,
    direction,
    client,
    token,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
}) => {
    const useCreditAccount = true;
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [rawGroups, setRawGroups] = useState([]);
    const [totalSupplierCount, setTotalSupplierCount] = useState(0);
    const [totalUnpaidCount, setTotalUnpaidCount] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [globalMethod, setGlobalMethod] = useState(2);
    const [supplierMethods, setSupplierMethods] = useState({});
    const [rowMethods, setRowMethods] = useState({});
    const [rowOverrides, setRowOverrides] = useState({});
    const [selection, setSelection] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [resultOpen, setResultOpen] = useState(false);
    const [createResults, setCreateResults] = useState([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [pendingEnterRowId, setPendingEnterRowId] = useState(null);
    const [creatingRowId, setCreatingRowId] = useState(null);
    const pendingEnterRowIdRef = useRef(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');
    const [editForm, setEditForm] = useState({
        amount: '',
        withholdingTax: '',
        paymentDate: dayjs(),
        description: '',
        paymentMethodType: 2,
    });

    const openDocumentPreview = useCallback((row) => {
        const url = row?.documentUrl
            ? `${row.documentUrl}#page=1&view=FitH`
            : `${NoDocumentsPdf}#page=1&view=FitH`;
        setPreviewUrl(url);
        setPreviewTitle(
            row?.reference
                || row?.description
                || row?.uploadNumber
                || (t('DocumentPreview') || 'Document Preview')
        );
        setPreviewOpen(true);
    }, [t]);

    const closeDocumentPreview = useCallback(() => {
        setPreviewOpen(false);
        setPreviewUrl('');
        setPreviewTitle('');
    }, []);

    useEffect(() => {
        pendingEnterRowIdRef.current = pendingEnterRowId;
    }, [pendingEnterRowId]);

    const resolveMethod = useCallback((row) => {
        const key = rowKey(row);
        if (rowMethods[key] !== undefined && rowMethods[key] !== null) return rowMethods[key];
        if (supplierMethods[row.supplierCardId] !== undefined) return supplierMethods[row.supplierCardId];
        return globalMethod;
    }, [rowMethods, supplierMethods, globalMethod]);

    const methodLabel = useCallback((value) => {
        const found = PAYMENT_METHODS.find((m) => m.value === value);
        if (!found) return '';
        return t(`PaymentMethod_${found.key}`) || found.key;
    }, [t]);

    const enrichRow = useCallback((row) => {
        const key = rowKey(row);
        const override = rowOverrides[key] || {};
        return {
            ...row,
            amount: override.amount !== undefined ? override.amount : row.amount,
            withholdingTax: override.withholdingTax !== undefined ? override.withholdingTax : row.withholdingTax,
            paymentDate: override.paymentDate || row.documentDate,
            description: override.description !== undefined ? override.description : row.description,
            paymentMethodType: resolveMethod(row),
        };
    }, [rowOverrides, resolveMethod]);

    const loadRequestIdRef = useRef(0);

    const loadData = useCallback(async () => {
        if (!client?.caseId || !token) return;
        const requestId = ++loadRequestIdRef.current;
        setLoading(true);
        try {
            const result = await ReportsService.getUnpaidSupplierTransactions({
                caseId: client.caseId,
                accountantId: client.accountantId,
                startDate: formatIsoDate(startDate),
                endDate: formatIsoDate(endDate),
                useCreditAccount,
                page: page + 1,
                pageSize,
                supplierCardId: selectedSupplier?.value || null,
                token,
            });

            // Ignore stale responses (StrictMode remount / rapid filter changes).
            // Do NOT abort the POST — aborting mid-body causes Kestrel
            // "Unexpected end of request content" 500s.
            if (requestId !== loadRequestIdRef.current) return;

            const groups = (result.supplierGroups || result.SupplierGroups || []).map(mapSupplierGroup);
            setRawGroups(groups);
            setTotalSupplierCount(result.totalSupplierCount ?? result.TotalSupplierCount ?? groups.length);
            setTotalUnpaidCount(result.totalCount ?? result.TotalCount ?? 0);
            setExpanded({});
            setSelection([]);

            const suppliers = (result.suppliers || result.Suppliers || []).map((s) => ({
                value: s.cardId || s.CardId,
                label: s.name || s.Name || s.cardId || s.CardId,
            }));
            setSupplierOptions(suppliers);
        } catch (error) {
            if (requestId !== loadRequestIdRef.current) return;
            console.error(error);
            toast.error(t('UnpaidPaymentsLoadFailed') || 'Failed to load unpaid transactions');
            setRawGroups([]);
            setTotalSupplierCount(0);
            setTotalUnpaidCount(0);
        } finally {
            if (requestId === loadRequestIdRef.current) setLoading(false);
        }
    }, [
        client?.caseId,
        client?.accountantId,
        token,
        startDate,
        endDate,
        useCreditAccount,
        selectedSupplier?.value,
        page,
        pageSize,
        t,
    ]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const supplierGroups = useMemo(() => rawGroups.map((g) => ({
        ...g,
        transactions: g.transactions.map(enrichRow),
    })), [rawGroups, enrichRow]);

    const enrichedRows = useMemo(
        () => supplierGroups.flatMap((g) => g.transactions),
        [supplierGroups]
    );

    const selectedRows = useMemo(
        () => enrichedRows.filter((r) => selection.includes(r.id)),
        [enrichedRows, selection]
    );

    const toggleExpand = (cardId) => {
        setExpanded((prev) => ({ ...prev, [cardId]: !prev[cardId] }));
    };

    const toggleSupplierSelection = (group, checked) => {
        const ids = group.transactions.map((r) => r.id);
        setSelection((prev) => {
            if (checked) return [...new Set([...prev, ...ids])];
            return prev.filter((id) => !ids.includes(id));
        });
    };

    const isSupplierFullySelected = (group) =>
        group.transactions.length > 0 && group.transactions.every((r) => selection.includes(r.id));

    const isSupplierPartiallySelected = (group) => {
        const n = group.transactions.filter((r) => selection.includes(r.id)).length;
        return n > 0 && n < group.transactions.length;
    };

    const updateRowOverride = useCallback((row, patch) => {
        const key = rowKey(row);
        setRowOverrides((prev) => ({
            ...prev,
            [key]: { ...(prev[key] || {}), ...patch },
        }));
    }, []);

    const openBatchEdit = () => {
        if (selectedRows.length === 0) return;
        const first = selectedRows[0];
        setEditForm({
            amount: selectedRows.length === 1 ? first.amount : '',
            withholdingTax: selectedRows.length === 1 ? first.withholdingTax : '',
            paymentDate: first.paymentDate ? dayjs(first.paymentDate) : dayjs(),
            description: selectedRows.length === 1 ? first.description : '',
            paymentMethodType: first.paymentMethodType,
        });
        setDrawerOpen(true);
    };

    const applyBatchEdit = () => {
        setRowOverrides((prev) => {
            const next = { ...prev };
            selectedRows.forEach((row) => {
                const key = rowKey(row);
                next[key] = {
                    ...(next[key] || {}),
                    ...(editForm.amount !== '' && editForm.amount !== null
                        ? { amount: Number(editForm.amount) }
                        : {}),
                    ...(editForm.withholdingTax !== '' && editForm.withholdingTax !== null
                        ? { withholdingTax: Number(editForm.withholdingTax) }
                        : {}),
                    paymentDate: editForm.paymentDate,
                    ...(editForm.description !== '' ? { description: editForm.description } : {}),
                };
            });
            return next;
        });
        setRowMethods((prev) => {
            const next = { ...prev };
            selectedRows.forEach((row) => {
                next[rowKey(row)] = editForm.paymentMethodType;
            });
            return next;
        });
        setDrawerOpen(false);
    };

    const buildRequestForRows = useCallback((rows) => {
        const supplierOverrides = {};
        Object.entries(supplierMethods).forEach(([cardId, method]) => {
            supplierOverrides[cardId] = method;
        });

        return {
            CaseId: client.caseId,
            AccountantId: client.accountantId,
            UseCreditAccount: useCreditAccount,
            GlobalPaymentMethodType: globalMethod,
            SupplierPaymentMethodOverrides: supplierOverrides,
            SkipAddToProcessed: true,
            Items: rows.map((row) => {
                const key = rowKey(row);
                const hasRowMethod = rowMethods[key] !== undefined && rowMethods[key] !== null;
                return {
                    SourceType: row.sourceType,
                    SourceId: row.sourceId,
                    PaymentMethodType: hasRowMethod ? rowMethods[key] : null,
                    Amount: row.amount,
                    WithholdingTax: row.withholdingTax,
                    PaymentDate: formatIsoDate(row.paymentDate),
                    Description: row.description || null,
                    ReportingMonthDate: formatIsoDate(row.reportingMonthDate),
                    RegistrationMonthDate: formatIsoDate(row.registrationMonthDate),
                    SupplierCardId: row.supplierCardId,
                };
            }),
        };
    }, [client, useCreditAccount, globalMethod, supplierMethods, rowMethods]);

    const createPaymentsForRows = useCallback(async (rows) => {
        if (!rows?.length || creating) return;
        setConfirmOpen(false);
        setPendingEnterRowId(null);
        setCreating(true);
        if (rows.length === 1) setCreatingRowId(rows[0].id);
        const allResults = [];
        try {
            const request = buildRequestForRows(rows);
            const chunks = [];
            for (let i = 0; i < request.Items.length; i += 50) {
                chunks.push(request.Items.slice(i, i + 50));
            }

            for (const chunk of chunks) {
                const response = await ReportsService.bulkCreatePaymentsFromSources(
                    { ...request, Items: chunk },
                    token
                );
                allResults.push(...(response.results || response.Results || []));
            }

            setCreateResults(allResults);
            if (allResults.length > 1 || !(allResults[0]?.success || allResults[0]?.Success)) {
                setResultOpen(true);
            }

            const successIds = new Set(
                allResults
                    .filter((r) => r.success || r.Success)
                    .map((r) => `${r.sourceType || r.SourceType}:${r.sourceId || r.SourceId}`)
            );

            setRawGroups((prev) => prev
                .map((g) => {
                    const transactions = g.transactions.filter((r) => !successIds.has(r.id));
                    return {
                        ...g,
                        transactions,
                        unpaidCount: transactions.length,
                        unpaidAmount: transactions.reduce((s, r) => s + (Number(r.amount) || 0), 0),
                    };
                })
                .filter((g) => g.transactions.length > 0));
            setSelection((prev) => prev.filter((id) => !successIds.has(id)));
            setTotalUnpaidCount((prev) => Math.max(0, prev - successIds.size));

            const ok = allResults.filter((r) => r.success || r.Success).length;
            const fail = allResults.length - ok;
            if (ok > 0) {
                toast.success(
                    (t('UnpaidPaymentsCreateSummary') || 'Created {ok}/{total} — {fail} failed')
                        .replace('{ok}', String(ok))
                        .replace('{total}', String(allResults.length))
                        .replace('{fail}', String(fail))
                );
            } else {
                toast.error(t('UnpaidPaymentsCreateFailed') || 'Bulk create failed');
            }
        } catch (error) {
            console.error(error);
            toast.error(t('UnpaidPaymentsCreateFailed') || 'Bulk create failed');
        } finally {
            setCreating(false);
            setCreatingRowId(null);
        }
    }, [creating, buildRequestForRows, token, t]);

    const handleCreate = () => createPaymentsForRows(selectedRows);

    const handleRowEnterKey = useCallback((row, e) => {
        if (e.key === 'Escape') {
            setPendingEnterRowId(null);
            return;
        }
        if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent?.isComposing) return;
        e.preventDefault();
        e.stopPropagation();
        if (creating) return;

        if (pendingEnterRowIdRef.current === row.id) {
            createPaymentsForRows([row]);
            return;
        }

        setPendingEnterRowId(row.id);
        toast.info(
            t('PressEnterAgainToCreatePayment') || 'Press Enter again to create this payment',
            { toastId: `enter-create-${row.id}`, autoClose: 4000 }
        );
    }, [creating, createPaymentsForRows, t]);

    const onRowFieldFocus = useCallback((rowId) => {
        if (pendingEnterRowIdRef.current && pendingEnterRowIdRef.current !== rowId) {
            setPendingEnterRowId(null);
        }
    }, []);

    const filteredSupplierForMethod = selectedSupplier?.value;

    return (
        <Box sx={{ width: '95%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Toolbar className={direction}>
                <div style={{ minWidth: 120, maxWidth: 140 }}>
                    <CustomMonthDatePicker
                        onDateChange={(d) => { setStartDate(d); setPage(0); }}
                        date={startDate}
                        label={t('StartDate')}
                        height="36px"
                    />
                </div>
                <div style={{ minWidth: 120, maxWidth: 140 }}>
                    <CustomMonthDatePicker
                        onDateChange={(d) => { setEndDate(d); setPage(0); }}
                        date={endDate}
                        label={t('EndDate')}
                        height="36px"
                    />
                </div>

                <div style={{ flex: '1 1 180px', minWidth: 160, maxWidth: 280 }}>
                    <AutocompleteSelect
                        options={supplierOptions}
                        selectedValues={selectedSupplier}
                        onChange={(_, newValue) => {
                            setSelectedSupplier(newValue || null);
                            setPage(0);
                        }}
                        label=""
                        placeholder={t('FilterBySupplier') || 'Filter supplier'}
                        isMultiple={false}
                        width="100%"
                        direction={direction}
                    />
                </div>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>{t('GlobalPaymentMethod') || 'Default method'}</InputLabel>
                    <Select
                        label={t('GlobalPaymentMethod') || 'Default method'}
                        value={globalMethod}
                        onChange={(e) => setGlobalMethod(Number(e.target.value))}
                    >
                        {PAYMENT_METHODS.map((m) => (
                            <MenuItem key={m.value} value={m.value}>
                                {methodLabel(m.value)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {filteredSupplierForMethod && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>{t('SupplierPaymentMethod') || 'Supplier method'}</InputLabel>
                        <Select
                            label={t('SupplierPaymentMethod') || 'Supplier method'}
                            value={supplierMethods[filteredSupplierForMethod] ?? ''}
                            displayEmpty
                            onChange={(e) => {
                                const v = e.target.value;
                                setSupplierMethods((prev) => {
                                    const next = { ...prev };
                                    if (v === '') delete next[filteredSupplierForMethod];
                                    else next[filteredSupplierForMethod] = Number(v);
                                    return next;
                                });
                            }}
                        >
                            <MenuItem value="">
                                <em>{t('UseGlobalDefault') || 'Use global'}</em>
                            </MenuItem>
                            {PAYMENT_METHODS.map((m) => (
                                <MenuItem key={m.value} value={m.value}>
                                    {methodLabel(m.value)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <ActionButton startIcon={<RefreshIcon />} onClick={() => loadData()} disabled={loading}>
                    {t('Refresh') || 'Refresh'}
                </ActionButton>

                <ActionButton
                    startIcon={<EditIcon />}
                    onClick={openBatchEdit}
                    disabled={selection.length === 0 || creating}
                >
                    {t('EditSelected') || 'Edit selected'} ({selection.length})
                </ActionButton>

                <ActionButton
                    startIcon={creating ? <CircularProgress size={14} color="inherit" /> : <PaymentIcon />}
                    onClick={() => setConfirmOpen(true)}
                    disabled={selection.length === 0 || creating}
                >
                    {t('CreateSelectedPayments') || 'Create payments'} ({selection.length})
                </ActionButton>

                <Typography variant="caption" sx={{ marginLeft: 'auto', fontWeight: 600, color: '#2c3e50' }}>
                    {t('TotalUnpaid') || 'Total'}: {totalUnpaidCount} | {t('Supplier') || 'Suppliers'}: {totalSupplierCount}
                </Typography>
            </Toolbar>

            <Paper sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', border: '1px solid #e3e8ff', borderRadius: '8px', overflow: 'hidden' }}>
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                    <Table size="small" stickyHeader dir={direction}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" sx={{ background: '#eef1ff', width: 40 }} />
                                <TableCell padding="checkbox" sx={{ background: '#eef1ff', width: 40 }} />
                                <TableCell sx={{ background: '#eef1ff', fontWeight: 700 }}>{t('CardId') || 'Card'}</TableCell>
                                <TableCell sx={{ background: '#eef1ff', fontWeight: 700 }}>{t('Supplier') || 'Supplier'}</TableCell>
                                <TableCell align="center" sx={{ background: '#eef1ff', fontWeight: 700 }}>{t('UnpaidCount') || 'Unpaid'}</TableCell>
                                <TableCell align="right" sx={{ background: '#eef1ff', fontWeight: 700 }}>{t('Debit') || 'Debit'}</TableCell>
                                <TableCell align="right" sx={{ background: '#eef1ff', fontWeight: 700 }}>{t('Credit') || 'Credit'}</TableCell>
                                <TableCell align="right" sx={{ background: '#eef1ff', fontWeight: 700 }}>{t('Balance') || 'Balance'}</TableCell>
                                <TableCell align="center" sx={{ background: '#eef1ff', fontWeight: 700 }}>{t('BalanceStatus') || 'Status'}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && supplierGroups.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#777' }}>
                                        {t('NoUnpaidTransactions') || 'No unpaid supplier transactions'}
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && supplierGroups.map((group) => {
                                const open = !!expanded[group.supplierCardId];
                                return (
                                    <React.Fragment key={group.supplierCardId}>
                                        <ParentRow onClick={() => toggleExpand(group.supplierCardId)}>
                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    size="small"
                                                    checked={isSupplierFullySelected(group)}
                                                    indeterminate={isSupplierPartiallySelected(group)}
                                                    onChange={(e) => toggleSupplierSelection(group, e.target.checked)}
                                                />
                                            </TableCell>
                                            <TableCell padding="checkbox">
                                                <IconButton size="small">
                                                    {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>{group.supplierCardId}</TableCell>
                                            <TableCell>{group.supplierCardName}</TableCell>
                                            <TableCell align="center">{group.unpaidCount}</TableCell>
                                            <TableCell align="right">{beautifyNumber(group.totalDebit)}</TableCell>
                                            <TableCell align="right">{beautifyNumber(group.totalCredit)}</TableCell>
                                            <TableCell align="right" sx={{ color: group.isClosed ? '#2e7d32' : '#d32f2f' }}>
                                                {beautifyNumber(group.balance)}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    size="small"
                                                    label={group.isClosed
                                                        ? (t('ClosedBalance') || 'Closed')
                                                        : (t('OpenBalance') || 'Open')}
                                                    sx={{
                                                        height: 22,
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        color: '#fff',
                                                        backgroundColor: group.isClosed ? '#2e7d32' : '#ed6c02',
                                                    }}
                                                />
                                            </TableCell>
                                        </ParentRow>
                                        <TableRow>
                                            <TableCell colSpan={9} sx={{ p: 0, borderBottom: open ? undefined : 'none' }}>
                                                <Collapse in={open} timeout="auto" unmountOnExit>
                                                    <Box sx={{ m: 1, ml: 4, mr: 1 }}>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow sx={{ background: '#fafbff' }}>
                                                                    <TableCell padding="checkbox" />
                                                                    <TableCell align="center" sx={{ width: 40 }}>{t('SerialNumber') || '#'}</TableCell>
                                                                    <TableCell>{t('Type') || 'Type'}</TableCell>
                                                                    <TableCell>{t('UploadNumber') || '#'}</TableCell>
                                                                    <TableCell>{t('PaymentDate') || t('Date') || 'Date'}</TableCell>
                                                                    <TableCell>{t('Reference') || 'Reference'}</TableCell>
                                                                    <TableCell>{t('Description') || 'Description'}</TableCell>
                                                                    <TableCell align="right">{t('Amount') || 'Amount'}</TableCell>
                                                                    <TableCell align="right">{t('WithholdingTax') || 'Withholding'}</TableCell>
                                                                    <TableCell>{t('PaymentMethod') || 'Method'}</TableCell>
                                                                    <TableCell align="center" sx={{ width: 96 }}>{t('Actions') || ''}</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {group.transactions.map((row, index) => {
                                                                    const isPendingEnter = pendingEnterRowId === row.id;
                                                                    const isCreatingThis = creatingRowId === row.id;
                                                                    return (
                                                                    <ChildRow
                                                                        key={row.id}
                                                                        sx={isPendingEnter ? {
                                                                            backgroundColor: 'rgba(102, 126, 234, 0.12)',
                                                                            outline: '2px solid #667eea',
                                                                            outlineOffset: -2,
                                                                        } : undefined}
                                                                    >
                                                                        <TableCell padding="checkbox">
                                                                            <Checkbox
                                                                                size="small"
                                                                                checked={selection.includes(row.id)}
                                                                                onChange={(e) => {
                                                                                    const checked = e.target.checked;
                                                                                    setSelection((prev) =>
                                                                                        checked
                                                                                            ? [...prev, row.id]
                                                                                            : prev.filter((id) => id !== row.id)
                                                                                    );
                                                                                }}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell align="center" sx={{ color: '#667', fontWeight: 600 }}>
                                                                            {index + 1}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {row.sourceType === 'Expenses'
                                                                                ? (t('Expenses') || 'Expenses')
                                                                                : (t('JournalEntries') || 'Journal')}
                                                                        </TableCell>
                                                                        <TableCell>{row.uploadNumber}</TableCell>
                                                                        <TableCell sx={{ minWidth: 140 }}>
                                                                            <TextField
                                                                                size="small"
                                                                                type="date"
                                                                                value={row.paymentDate ? dayjs(row.paymentDate).format('YYYY-MM-DD') : ''}
                                                                                onChange={(e) => updateRowOverride(row, {
                                                                                    paymentDate: e.target.value ? dayjs(e.target.value) : null,
                                                                                })}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                onFocus={() => onRowFieldFocus(row.id)}
                                                                                onKeyDown={(e) => handleRowEnterKey(row, e)}
                                                                                fullWidth
                                                                                sx={inlineFieldSx}
                                                                                inputProps={{ 'aria-label': t('Date') || 'Date' }}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>{row.reference || '-'}</TableCell>
                                                                        <TableCell sx={{ minWidth: 160 }}>
                                                                            <TextField
                                                                                size="small"
                                                                                value={row.description || ''}
                                                                                onChange={(e) => updateRowOverride(row, { description: e.target.value })}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                onFocus={() => onRowFieldFocus(row.id)}
                                                                                onKeyDown={(e) => handleRowEnterKey(row, e)}
                                                                                fullWidth
                                                                                placeholder="-"
                                                                                sx={inlineFieldSx}
                                                                                inputProps={{ 'aria-label': t('Description') || 'Description' }}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ minWidth: 110 }}>
                                                                            <TextField
                                                                                size="small"
                                                                                type="number"
                                                                                value={row.amount ?? ''}
                                                                                onChange={(e) => {
                                                                                    const v = e.target.value;
                                                                                    updateRowOverride(row, {
                                                                                        amount: v === '' ? 0 : Number(v),
                                                                                    });
                                                                                }}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                onFocus={(e) => {
                                                                                    e.target.select();
                                                                                    onRowFieldFocus(row.id);
                                                                                }}
                                                                                onKeyDown={(e) => handleRowEnterKey(row, e)}
                                                                                fullWidth
                                                                                sx={{
                                                                                    ...inlineFieldSx,
                                                                                    '& .MuiInputBase-input': { ...inlineFieldSx['& .MuiInputBase-input'], textAlign: 'right' },
                                                                                }}
                                                                                inputProps={{ step: '0.01', 'aria-label': t('Amount') || 'Amount' }}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ minWidth: 100 }}>
                                                                            <TextField
                                                                                size="small"
                                                                                type="number"
                                                                                value={row.withholdingTax ?? ''}
                                                                                onChange={(e) => {
                                                                                    const v = e.target.value;
                                                                                    updateRowOverride(row, {
                                                                                        withholdingTax: v === '' ? 0 : Number(v),
                                                                                    });
                                                                                }}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                onFocus={(e) => {
                                                                                    e.target.select();
                                                                                    onRowFieldFocus(row.id);
                                                                                }}
                                                                                onKeyDown={(e) => handleRowEnterKey(row, e)}
                                                                                fullWidth
                                                                                sx={{
                                                                                    ...inlineFieldSx,
                                                                                    '& .MuiInputBase-input': { ...inlineFieldSx['& .MuiInputBase-input'], textAlign: 'right' },
                                                                                }}
                                                                                inputProps={{ step: '0.01', 'aria-label': t('WithholdingTax') || 'Withholding' }}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Select
                                                                                size="small"
                                                                                value={row.paymentMethodType}
                                                                                onChange={(e) => {
                                                                                    const value = Number(e.target.value);
                                                                                    setRowMethods((prev) => ({ ...prev, [rowKey(row)]: value }));
                                                                                }}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                onFocus={() => onRowFieldFocus(row.id)}
                                                                                onKeyDown={(e) => handleRowEnterKey(row, e)}
                                                                                MenuProps={{
                                                                                    sx: { zIndex: (theme) => theme.zIndex.modal + 4 },
                                                                                }}
                                                                                sx={{ fontSize: '12px', height: 28, minWidth: 120 }}
                                                                            >
                                                                                {PAYMENT_METHODS.map((m) => (
                                                                                    <MenuItem key={m.value} value={m.value}>
                                                                                        {methodLabel(m.value)}
                                                                                    </MenuItem>
                                                                                ))}
                                                                            </Select>
                                                                        </TableCell>
                                                                        <TableCell align="center">
                                                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Tooltip title={t('ViewDocument') || t('DocumentPreview') || 'View document'}>
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        color="primary"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            openDocumentPreview(row);
                                                                                        }}
                                                                                        sx={{
                                                                                            background: 'rgba(48, 79, 255, 0.1)',
                                                                                            color: '#304FFF',
                                                                                            '&:hover': {
                                                                                                background: 'rgba(48, 79, 255, 0.2)',
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        <VisibilityIcon sx={{ fontSize: 18 }} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                                <Tooltip title={t('CreatePaymentRow') || 'Create payment'}>
                                                                                    <span>
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            color="primary"
                                                                                            disabled={creating}
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                createPaymentsForRows([row]);
                                                                                            }}
                                                                                            sx={{
                                                                                                background: isPendingEnter
                                                                                                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                                                                                    : 'rgba(102, 126, 234, 0.12)',
                                                                                                color: isPendingEnter ? '#fff' : '#5a6fd8',
                                                                                                '&:hover': {
                                                                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                                                                    color: '#fff',
                                                                                                },
                                                                                            }}
                                                                                        >
                                                                                            {isCreatingThis
                                                                                                ? <CircularProgress size={14} color="inherit" />
                                                                                                : <PaymentIcon sx={{ fontSize: 18 }} />}
                                                                                        </IconButton>
                                                                                    </span>
                                                                                </Tooltip>
                                                                            </Box>
                                                                        </TableCell>
                                                                    </ChildRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalSupplierCount}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={(e) => {
                        setPageSize(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage={t('Rows per page')}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} ${t('of')} ${count !== -1 ? count : `${t('more than')} ${to}`}`
                    }
                    sx={{
                        borderTop: '1px solid #e3e8ff',
                        '& .MuiTablePagination-toolbar': { minHeight: 40 },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: 12 },
                    }}
                />
            </Paper>

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
                sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
            >
                <DialogTitle>{t('ConfirmCreatePayments') || 'Create payments?'}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 1 }}>
                        {(t('ConfirmCreatePaymentsBody') || 'Create {count} payment(s) with default method: {method}')
                            .replace('{count}', String(selection.length))
                            .replace('{method}', methodLabel(globalMethod))}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('ConfirmCreatePaymentsHint') || 'Row and supplier overrides will be applied where set.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>{t('Cancel') || 'Cancel'}</Button>
                    <Button variant="contained" onClick={handleCreate}>{t('Create') || 'Create'}</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={previewOpen}
                onClose={closeDocumentPreview}
                maxWidth="lg"
                fullWidth
                sx={{ zIndex: (theme) => theme.zIndex.modal + 3 }}
                PaperProps={{
                    sx: {
                        height: '85vh',
                        maxHeight: '85vh',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        py: 1.25,
                        direction,
                    }}
                >
                    <Typography component="span" sx={{ fontWeight: 700, fontSize: 16 }}>
                        {t('DocumentPreview') || 'Document Preview'}
                        {previewTitle ? ` — ${previewTitle}` : ''}
                    </Typography>
                    <IconButton size="small" onClick={closeDocumentPreview} aria-label={t('Close') || 'Close'}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, flex: 1, minHeight: 0 }}>
                    {previewUrl ? (
                        <iframe
                            key={previewUrl}
                            src={previewUrl}
                            title={previewTitle || 'Document'}
                            width="100%"
                            height="100%"
                            style={{ border: 'none', display: 'block', minHeight: '70vh' }}
                            allowFullScreen
                        />
                    ) : null}
                </DialogContent>
            </Dialog>

            <Dialog
                open={resultOpen}
                onClose={() => setResultOpen(false)}
                maxWidth="md"
                fullWidth
                sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
            >
                <DialogTitle>{t('CreatePaymentsResults') || 'Create results'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                        {createResults.map((r, idx) => {
                            const ok = r.success || r.Success;
                            const sourceId = r.sourceId || r.SourceId;
                            const uploadNumber = r.uploadNumber ?? r.UploadNumber;
                            const error = r.error || r.Error;
                            return (
                                <Typography
                                    key={`${sourceId}-${idx}`}
                                    sx={{ color: ok ? 'success.main' : 'error.main', fontSize: 13, mb: 0.5 }}
                                >
                                    {ok
                                        ? `✓ ${sourceId} → #${uploadNumber}`
                                        : `✗ ${sourceId}: ${error || 'failed'}`}
                                </Typography>
                            );
                        })}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResultOpen(false)}>{t('Close') || 'Close'}</Button>
                </DialogActions>
            </Dialog>

            <Drawer
                anchor={direction === 'rtl' ? 'left' : 'right'}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                // Above WithholdingModal (MUI modal z-index 1300); default Drawer is 1200.
                sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
                ModalProps={{
                    sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
                }}
                PaperProps={{ sx: { width: 340, p: 2, zIndex: (theme) => theme.zIndex.modal + 3 } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography fontWeight={700}>
                        {t('EditSelected') || 'Edit selected'} ({selection.length})
                    </Typography>
                    <IconButton size="small" onClick={() => setDrawerOpen(false)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <TextField
                    label={t('Amount') || 'Amount'}
                    type="number"
                    size="small"
                    fullWidth
                    value={editForm.amount}
                    onChange={(e) => setEditForm((p) => ({ ...p, amount: e.target.value }))}
                    sx={{ mb: 1.5 }}
                    helperText={selection.length > 1 ? (t('LeaveBlankToKeep') || 'Leave blank to keep existing') : ''}
                />
                <TextField
                    label={t('WithholdingTax') || 'Withholding'}
                    type="number"
                    size="small"
                    fullWidth
                    value={editForm.withholdingTax}
                    onChange={(e) => setEditForm((p) => ({ ...p, withholdingTax: e.target.value }))}
                    sx={{ mb: 1.5 }}
                />
                <TextField
                    label={t('Description') || 'Description'}
                    size="small"
                    fullWidth
                    value={editForm.description}
                    onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                    sx={{ mb: 1.5 }}
                />
                <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                    <InputLabel>{t('PaymentMethod') || 'Method'}</InputLabel>
                    <Select
                        label={t('PaymentMethod') || 'Method'}
                        value={editForm.paymentMethodType}
                        onChange={(e) => setEditForm((p) => ({ ...p, paymentMethodType: Number(e.target.value) }))}
                    >
                        {PAYMENT_METHODS.map((m) => (
                            <MenuItem key={m.value} value={m.value}>
                                {methodLabel(m.value)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <ActionButton fullWidth onClick={applyBatchEdit}>
                    {t('Apply') || 'Apply'}
                </ActionButton>
            </Drawer>
        </Box>
    );
};

export default WithholdingUnpaidPaymentsTab;
